import React from 'react';
import PropTypes from 'prop-types';
import { css } from 'twin.macro';
import { motion } from 'framer-motion';
import Button from '../components/Button/Button';
import SVGIcon from '../components/SVGIcon/SVGIcon';
import { Meta } from '../styled components/Typography';
import ContainerWrapper from '../styled components/ContainerWrapper';
import Table from '../components/Table';
import TableListItem from '../components/TableListItem';
import {
  createEmptyEventsFilter,
  createNoFilter,
  getDefaultFilter,
  isNoFilter,
  setFilterAsDefault
} from '../libs/eventsFilters';
import { NavigationVariants } from '../libs/animations';
import debounce from '../libs/debounce';

export default class EventsFiltersList extends React.Component {
  constructor(props) {
    super(props);

    const { filters } = this.props;
    this.noFilterOption = createNoFilter();
    if (isNoFilter(getDefaultFilter(filters))) {
      setFilterAsDefault(this.noFilterOption);
    }

    this.handleAddFilterClick = debounce(this.handleAddFilterClick.bind(this));
    this.handleInfoButtonClick = debounce(this.handleInfoButtonClick.bind(this));
  }

  getIndexOfDefaultFilter() {
    const { filters } = this.props;
    const idxOfDefaultFilter = filters.findIndex((f) => f.isDefault);
    return idxOfDefaultFilter === -1 ? 0 : filters.findIndex((f) => f.isDefault) + 1;
  }

  changeFilterSelection(filter) {
    const { handleSetFilterActive } = this.props;
    handleSetFilterActive(filter);
  }

  handleAddFilterClick(event) {
    const { filters, setUserPreferenceEventFilters, openFilterEditor } = this.props;
    event.stopPropagation();
    const updatedFilters = [...filters, createEmptyEventsFilter()];
    setUserPreferenceEventFilters(
      updatedFilters,
      () => {
        openFilterEditor(updatedFilters.length - 1);
      },
      false
    );
  }

  handleInfoButtonClick(filter) {
    const { filters, openFilterEditor } = this.props;
    /* passing editingExistingFilter=true to the filter editor will make sure that the filter
       will not be deleted when cancelling the changes
    */
    openFilterEditor(filters.indexOf(filter), true);
  }

  renderInfoButton(filter) {
    const onClick = (e) => {
      e.stopPropagation();
      this.handleInfoButtonClick(filter);
    };
    const onKeyDown = (e) => e.keyCode !== 13 || onClick(e);
    return (
      <Button
        contentType="icon"
        size="medium"
        icon="info"
        color="default"
        onClick={onClick}
        onKeyDown={onKeyDown}
      />
    );
  }

  renderAddFilterButton() {
    return (
      <Button
        contentType="toggle"
        icon="add-circle-outline"
        iconPosition="right"
        color="primary"
        data-test-add-filter-button-filterlist
        onClick={this.handleAddFilterClick}
      >
        {i18n('Add Filter')}
      </Button>
    );
  }

  renderCheckmark = (optionIndex, optionSelected) => {
    if (optionIndex === optionSelected) {
      return (
        <div
          css={css`
            margin-left: -36px;
          `}
        >
          <p
            tw="flex text-blue-base "
            css={css`
              padding: 0.375rem;
            `}
          >
            <SVGIcon size="large" icon="check" />
          </p>
        </div>
      );
    }
    return null;
  };

  render() {
    const { filters, useMotion } = this.props;

    return (
      <motion.div
        initial="toggleFromTopInvisible"
        animate={useMotion ? 'toggleFromTopVisible' : 'toggleFromTopVisibleNoTransition'}
        exit="toggleFromTopInvisible"
        variants={NavigationVariants}
      >
        <ContainerWrapper tw="px-0">
          <Meta as="p" $color="gray dark" $transform="uppercase" tw="mb-4 pl-16">
            {i18n('Select Filter')}
          </Meta>
        </ContainerWrapper>
        <ContainerWrapper
          tw="px-0 border-t border-b border-silver-base"
          data-test-type="events-filterlist"
        >
          {/* Max-height is defined so a user cannot get stuck (being unable to scroll the page)
           * when there are a lot of defined filters and the filterlist (inside a fixed EventsActionbar) becomes so long it fills the page height.
           * The value is based on the height of the filter item 'No filter' + the sum of the height of 3.5 filter items.
           * It's defined on a wrapping element for maintenability, if other elements apart from the Table component
           * need to be included in the scrollable area they can easily be added to the wrapper */}
          <div
            tw="overflow-y-auto"
            css={css`
              max-height: 15.44rem;
            `}
          >
            <Table>
              {[this.noFilterOption, ...filters].map((filter, index) => (
                <TableListItem
                  label={filter.name}
                  css={css`
                    width: calc(100% - 24px);
                  `}
                  tw="ml-24 "
                  leftElement={this.renderCheckmark(
                    index,
                    this.getIndexOfDefaultFilter()
                  )}
                  rightElement={
                    !isNoFilter(filter) ? this.renderInfoButton(filter) : <></>
                  }
                  onClick={() => this.changeFilterSelection(filter)}
                  key={`events-filter-list-item-${filter.name}${index}`}
                />
              ))}
            </Table>
          </div>

          <div tw="bg-white">
            <span
              tw="block bg-silver-base  h-px ml-40"
              css={css`
                width: calc(100% - 3.5rem);
              `}
            />
            <div
              tw="flex justify-end py-4 "
              css={css`
                padding-right: 1.125rem;
              `}
            >
              {this.renderAddFilterButton()}
            </div>
          </div>
        </ContainerWrapper>
      </motion.div>
    );
  }
}

EventsFiltersList.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      isDefault: PropTypes.bool,
      locations: PropTypes.arrayOf(PropTypes.string),
      statuses: PropTypes.arrayOf(PropTypes.string),
      profiles: PropTypes.arrayOf(PropTypes.string)
    })
  ).isRequired,
  setUserPreferenceEventFilters: PropTypes.func.isRequired,
  openFilterEditor: PropTypes.func.isRequired,
  handleSetFilterActive: PropTypes.func.isRequired,
  useMotion: PropTypes.bool
};

EventsFiltersList.defaultProps = {
  useMotion: true
};
